import { StationView } from "./../utils/StationView";
import { SessionConfirmationModal } from "./../utils/SessionConfirmationModal";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { trackAnalyticsEvent } from "../utils/analytics";
import { getCurrentUsername } from "../utils/auth";

const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_LOCAL_API_URL;
const DEFAULT_VIDEO_PATH = "Ingen video vald";
const MANAGE_SESSION_DRAFT_STORAGE_PREFIX = "manageSessionDraft";

function createEmptyStationDraft() {
  return {
    title: "",
    description: "",
    videoPath: DEFAULT_VIDEO_PATH
  };
}

function moveItem(list, fromIndex, toIndex) {
  const updatedList = [...list];
  const [movedItem] = updatedList.splice(fromIndex, 1);
  updatedList.splice(toIndex, 0, movedItem);
  return updatedList;
}

function SessionsViewer({ exercise, setExercise, userRole }) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const currentUsername = getCurrentUsername();
  const draggedStationId = useRef(null);
  const hasHydratedDraft = useRef(false);
  const hasTrackedSessionView = useRef(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState(state?.success || "");
  const [session, setSession] = useState({
    station: []
  });
  const [rerender, setRerender] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [isAddingStation, setIsAddingStation] = useState(false);
  const [newStation, setNewStation] = useState(createEmptyStationDraft);
  const [adminGroups, setAdminGroups] = useState([]);
  const [shareGroupId, setShareGroupId] = useState("");

  const draftStorageKey = state?.session
    ? `${MANAGE_SESSION_DRAFT_STORAGE_PREFIX}:${state.session}`
    : "";

  const fetchSession = useCallback(async () => {
    if (!state?.session) {
      setErrorMessage("Ingen data tillgänglig");
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/training/session-by-id`,
        {
          headers: {
            token: localStorage.getItem("token")
          },
          params: {
            sessionId: state.session
          }
        });
      setSession(response.data);
      setErrorMessage("");

      if (!hasTrackedSessionView.current) {
        hasTrackedSessionView.current = true;
        trackAnalyticsEvent("/analytics/track-session-view", {
          sessionId: response.data._id
        });
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Fel vid hämtning av data");
    }
  }, [state?.session]);

  const fetchAdminGroups = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/groups/my-groups`, {
        headers: {
          token: localStorage.getItem("token")
        }
      });
      setAdminGroups(response.data.administeredGroups || []);
    } catch (error) {
      setAdminGroups([]);
    }
  }, []);

  async function persistStationOrder(nextStations, fallbackStations) {
    try {
      setIsReordering(true);
      const response = await axios.patch(`${API_URL}/training/reorder-stations`,
        {
          sessionId: state.session,
          stationIds: nextStations.map((station) => station._id)
        },
        {
          headers: {
            "Content-Type": "application/json",
            token: localStorage.getItem("token")
          }
        });

      setSession(response.data);
      setSuccessMessage("Stationsordningen uppdaterades.");
      setErrorMessage("");
    } catch (error) {
      setSession((previousSession) => ({
        ...previousSession,
        station: fallbackStations
      }));
      setErrorMessage(error.response?.data?.message || "Kunde inte uppdatera stationsordningen");
    } finally {
      setIsReordering(false);
    }
  }

  function handleDragStart(stationId) {
    draggedStationId.current = stationId;
  }

  function handleDrop(targetStationId) {
    if (!draggedStationId.current || draggedStationId.current === targetStationId || isReordering) {
      draggedStationId.current = null;
      return;
    }

    const previousStations = [...session.station];
    const sourceIndex = previousStations.findIndex((station) => station._id === draggedStationId.current);
    const targetIndex = previousStations.findIndex((station) => station._id === targetStationId);
    if (sourceIndex < 0 || targetIndex < 0) {
      draggedStationId.current = null;
      return;
    }

    const nextStations = moveItem(previousStations, sourceIndex, targetIndex);
    setSession((previousSession) => ({
      ...previousSession,
      station: nextStations
    }));
    draggedStationId.current = null;
    persistStationOrder(nextStations, previousStations);
  }

  async function duplicateSession() {
    try {
      setDuplicating(true);
      const response = await axios.post(`${API_URL}/training/duplicate-session-by-id`,
        { sessionId: state.session },
        {
          headers: {
            "Content-Type": "application/json",
            token: localStorage.getItem("token")
          }
        });

      setSuccessMessage(`"${response.data.title}" skapades.`);
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Kunde inte duplicera passet");
    } finally {
      setDuplicating(false);
    }
  }

  async function shareSessionWithGroup(event) {
    event.preventDefault();

    if (!shareGroupId) {
      setErrorMessage("Välj en grupp att dela passet med");
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/training/share-session-with-group`,
        {
          sessionId: state.session,
          groupId: shareGroupId
        },
        {
          headers: {
            "Content-Type": "application/json",
            token: localStorage.getItem("token")
          }
        });

      setSession(response.data.session);
      setShareGroupId("");
      setSuccessMessage("Passet delades med gruppen.");
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Kunde inte dela passet med gruppen");
      setSuccessMessage("");
    }
  }

  async function unshareSessionWithGroup(groupId) {
    try {
      const response = await axios.delete(`${API_URL}/training/unshare-session-with-group`, {
        headers: {
          "Content-Type": "application/json",
          token: localStorage.getItem("token")
        },
        data: {
          sessionId: state.session,
          groupId
        }
      });

      setSession(response.data.session);
      setSuccessMessage("Gruppdelningen togs bort.");
      setErrorMessage("");
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Kunde inte ta bort gruppdelningen");
      setSuccessMessage("");
    }
  }

  function resetNewStation() {
    setNewStation(createEmptyStationDraft());
    setExercise((previous) => ({
      ...previous,
      videoPath: DEFAULT_VIDEO_PATH
    }));
    if (draftStorageKey) {
      localStorage.removeItem(draftStorageKey);
    }
  }

  async function addStationToSession(event) {
    event.preventDefault();

    if (!newStation.title.trim()) {
      setErrorMessage("Ange en titel för stationen");
      return;
    }

    if (newStation.videoPath === DEFAULT_VIDEO_PATH) {
      setErrorMessage("Välj en video för stationen");
      return;
    }

    try {
      setIsAddingStation(true);
      await axios.post(`${API_URL}/training/add-station`,
        {
          sessionId: state.session,
          title: newStation.title.trim(),
          description: newStation.description.trim(),
          videoPath: newStation.videoPath
        },
        {
          headers: {
            "Content-Type": "application/json",
            token: localStorage.getItem("token")
          }
        });

      resetNewStation();
      setSuccessMessage("Stationen lades till i passet.");
      setErrorMessage("");
      setRerender((previous) => !previous);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Kunde inte lägga till stationen");
    } finally {
      setIsAddingStation(false);
    }
  }

  useEffect(() => {
    fetchSession();
  }, [fetchSession, rerender]);

  useEffect(() => {
    fetchAdminGroups();
  }, [fetchAdminGroups]);

  useEffect(() => {
    if (!draftStorageKey) {
      hasHydratedDraft.current = true;
      return;
    }

    const storedDraft = localStorage.getItem(draftStorageKey);
    if (storedDraft) {
      try {
        const parsedDraft = JSON.parse(storedDraft);
        setNewStation({
          ...createEmptyStationDraft(),
          ...parsedDraft
        });
      } catch (error) {
        localStorage.removeItem(draftStorageKey);
      }
    }

    hasHydratedDraft.current = true;
  }, [draftStorageKey]);

  useEffect(() => {
    if (!hasHydratedDraft.current || !draftStorageKey) {
      return;
    }

    localStorage.setItem(draftStorageKey, JSON.stringify(newStation));
  }, [draftStorageKey, newStation]);

  useEffect(() => {
    if (exercise.videoPath && exercise.videoPath !== DEFAULT_VIDEO_PATH && exercise.videoPath !== newStation.videoPath) {
      setNewStation((previous) => ({
        ...previous,
        videoPath: exercise.videoPath
      }));
      setExercise((previous) => ({
        ...previous,
        videoPath: DEFAULT_VIDEO_PATH
      }));
    }
  }, [exercise.videoPath, newStation.videoPath, setExercise]);

  const canEditSession = (session.owners || []).includes(currentUsername) || userRole === 1 || userRole === 2;
  const canShareWithGroups = canEditSession && [1, 2, 4, 5].includes(userRole);
  const sharedGroupIds = (session.sharedWithGroups || []).map((groupId) => String(groupId));
  const availableGroups = adminGroups.filter((group) => !sharedGroupIds.includes(String(group._id)));
  const sharedGroups = adminGroups.filter((group) => sharedGroupIds.includes(String(group._id)));

  return (
    <div className="flex flex-col items-center mx-2">
      <div className="mx-4 my-4 w-full max-w-3xl rounded-[28px] bg-gradient-to-br from-slate-50 via-white to-sky-50 px-4 py-4 shadow-sm ring-1 ring-slate-200/80">
        {session && (
          <div className="rounded-[24px] border border-slate-200 bg-white/85 px-4 py-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="truncate text-lg font-semibold text-slate-800">Titel: {session.title}</div>
                <div className="mt-1 text-sm text-slate-600">Beskrivning: {session.description || "Ingen beskrivning"}</div>
                <div className="mt-3 text-xs font-medium uppercase tracking-[0.16em] text-sky-700">
                  Dra och släpp stationerna för att ändra ordning
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  className="inline-block rounded-[20px] border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-100"
                  onClick={() => navigate("/my-workouts")}
                >
                  Till mina pass
                </button>
                <button
                  className="inline-block rounded-[20px] border border-sky-200/80 bg-gradient-to-r from-sky-700 to-cyan-500 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                  onClick={() => {
                    setSuccessMessage("");
                    setErrorMessage("");
                    document.getElementById("manage-session-add-station")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  disabled={!canEditSession}
                >
                  Ny station
                </button>
                <button
                  className="inline-block rounded-[20px] border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={duplicateSession}
                  disabled={duplicating}
                >
                  {duplicating ? "Duplicerar..." : "Duplicera pass"}
                </button>
                <button
                  className="inline-block rounded-[20px] border border-sky-200 bg-white px-5 py-3 text-sm font-semibold text-sky-700 shadow-sm transition-colors hover:bg-sky-50"
                  onClick={() => navigate("/createsession", { state: { templateSessionId: session._id } })}
                >
                  Använd som mall
                </button>
                {canEditSession && <SessionConfirmationModal state={state} />}
              </div>
            </div>
          </div>
        )}

        {canShareWithGroups && (
        <div className="mt-4 rounded-[24px] border border-slate-200 bg-white/85 px-4 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-700">Dela pass med grupp</p>
              <p className="mt-1 text-sm text-slate-500">Medlemmar i gruppen får passet i sin passlista och kan köra det.</p>
            </div>
          </div>

          <form className="mt-4 flex flex-col gap-3 sm:flex-row" onSubmit={shareSessionWithGroup}>
            <select
              className="theme-input flex-1"
              value={shareGroupId}
              onChange={(event) => setShareGroupId(event.target.value)}
            >
              <option value="">Välj grupp</option>
              {availableGroups.map((group) => (
                <option key={group._id} value={group._id}>
                  {group.name}
                </option>
              ))}
            </select>
            <button
              className="rounded-[20px] border border-sky-200/80 bg-gradient-to-r from-sky-700 to-cyan-500 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
              type="submit"
              disabled={!shareGroupId}
            >
              Dela
            </button>
          </form>

          <div className="mt-4 flex flex-wrap gap-2">
            {sharedGroups.length === 0 && (
              <span className="text-sm text-slate-500">Passet är inte delat med någon av dina grupper.</span>
            )}
            {sharedGroups.map((group) => (
              <span key={group._id} className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">
                {group.name}
                <button
                  className="rounded-full border border-emerald-200 bg-white px-2 py-1 text-xs text-emerald-800"
                  onClick={() => unshareSessionWithGroup(group._id)}
                  type="button"
                >
                  Ta bort
                </button>
              </span>
            ))}
          </div>
        </div>
        )}

        {!canEditSession && (
          <div className="mt-4 rounded-[24px] border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
            Det här passet är delat med dig via en grupp. Du kan köra passet och spara egna anteckningar, men bara ägaren kan ändra stationer och delningar.
          </div>
        )}

        {canEditSession && (
        <div id="manage-session-add-station" className="mt-4 rounded-[24px] border border-slate-200 bg-white/85 px-4 py-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-700">Lägg till station i passet</p>
              <p className="mt-1 text-sm text-slate-500">Stanna kvar i passet medan du lägger till nya stationer.</p>
            </div>
            <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-sky-700">
              Direkt i passet
            </span>
          </div>

          <form className="mt-4 grid gap-3" onSubmit={addStationToSession}>
            <input
              value={newStation.title}
              className="rounded-2xl border border-sky-200 bg-white px-5 py-3 shadow-sm outline-none transition-colors focus:border-sky-400"
              placeholder="Stationstitel"
              onChange={(event) => setNewStation({ ...newStation, title: event.target.value })}
            />
            <textarea
              value={newStation.description}
              className="min-h-[96px] rounded-2xl border border-sky-200 bg-white px-5 py-3 shadow-sm outline-none transition-colors focus:border-sky-400"
              placeholder="Stationsbeskrivning"
              onChange={(event) => setNewStation({ ...newStation, description: event.target.value })}
            />
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                className="flex-1 rounded-2xl border border-sky-200 bg-white px-5 py-3 text-slate-500 shadow-sm"
                value={newStation.videoPath}
                disabled
              />
              <button
                type="button"
                className="rounded-[20px] border border-sky-200/80 bg-white px-5 py-3 text-sm font-semibold text-sky-700 shadow-sm transition-colors hover:bg-sky-50"
                onClick={() => navigate("/videos", { state: { source: "manage-session" } })}
              >
                Välj video
              </button>
              <button
                type="submit"
                className="rounded-[20px] border border-sky-200/80 bg-gradient-to-r from-sky-700 to-cyan-500 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
                disabled={isAddingStation}
              >
                {isAddingStation ? "Lägger till..." : "Spara station"}
              </button>
            </div>
          </form>
        </div>
        )}

        {successMessage && (
          <div className="mx-auto my-4 w-full rounded-2xl border border-green-300 bg-green-50 px-5 py-3 text-center text-sm text-green-800">
            {successMessage}
          </div>
        )}
        {errorMessage && (
          <div className="mx-auto my-4 w-full rounded-2xl border border-red-300 bg-red-50 px-5 py-3 text-center text-sm text-red-800">
            {errorMessage}
          </div>
        )}

        {session && session.station.length === 0 && !errorMessage && (
          <div className="mt-4 rounded-[22px] border border-dashed border-slate-300 bg-white/80 px-4 py-6 text-center text-sm text-slate-500">
            Det finns inga stationer i passet ännu.
          </div>
        )}

        {session && session.station.map((station, index) => (
          <div key={station._id}>
            <StationView
              station={station}
              state={state}
              rerender={rerender}
              setRerender={setRerender}
              draggable={!isReordering && canEditSession}
              showDelete={canEditSession}
              onDragStart={() => handleDragStart(station._id)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => handleDrop(station._id)}
              index={index}
            />
          </div>
        ))}

        {isReordering && (
          <div className="mt-3 rounded-2xl border border-sky-200 bg-sky-50 px-5 py-3 text-center text-sm text-sky-800">
            Sparar ny ordning...
          </div>
        )}
      </div>
    </div>
  );
}

export default SessionsViewer;
